import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ContentCard, ContentCardSkeleton } from '@/components/ContentCard';
import { Gamepad2, Film, Zap, TrendingUp, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage({ setTheaterContent }) {
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await fetch(`${API}/content/featured/home`);
        if (response.ok) {
          const data = await response.json();
          setFeatured(data);
        }
      } catch (error) {
        console.error('Failed to fetch featured:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeatured();
  }, []);

  const handleContentClick = async (content) => {
    try {
      const response = await fetch(`${API}/content/${content.content_id}`);
      if (response.ok) {
        const data = await response.json();
        setTheaterContent(data);
      }
    } catch (error) {
      console.error('Failed to load content:', error);
    }
  };

  return (
    <div className="space-y-8" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-[#1a0000] to-[#050505] border border-[#330000] p-8 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative z-10 text-center">
          <h1 className="text-4xl md:text-6xl font-black mb-4">
            <span className="ws-logo-war">WAR</span>
            <span className="ws-logo-syndicate">SYNDICATE</span>
          </h1>
          <p className="text-[#999] text-sm md:text-base max-w-xl mx-auto mb-6">
            The underground portal for Flash games and animations. Upload your creations, 
            survive the Vortex, and become a legend.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/vortex" className="btn-primary flex items-center gap-2" data-testid="hero-vortex-btn">
              <Zap size={16} />
              ENTER THE VORTEX
            </Link>
            <Link to="/upload" className="btn-secondary flex items-center gap-2" data-testid="hero-upload-btn">
              SUBMIT YOUR WORK
            </Link>
          </div>
        </div>
      </section>

      {/* Under Judgment - The Vortex Preview */}
      {featured?.vortex?.length > 0 && (
        <section data-testid="vortex-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Zap size={18} className="text-[#cc0000]" />
              <span className="text-[#cc0000]">THE VORTEX</span>
              <span className="text-[#666] text-xs ml-2">UNDER JUDGMENT</span>
            </h2>
            <Link to="/vortex" className="text-xs text-[#ffcc00] hover:text-[#ffd700]">
              VIEW ALL &gt;&gt;
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {loading ? (
              Array(6).fill(0).map((_, i) => <ContentCardSkeleton key={i} />)
            ) : (
              featured.vortex.map((content) => (
                <ContentCard 
                  key={content.content_id}
                  content={content}
                  onClick={() => handleContentClick(content)}
                />
              ))
            )}
          </div>
        </section>
      )}

      {/* Two column layout for games and videos */}
      <div className="grid md:grid-cols-2 gap-8">
        {/* Top Games */}
        <section data-testid="top-games-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Gamepad2 size={18} className="text-[#cc0000]" />
              TOP GAMES
            </h2>
            <Link to="/games" className="text-xs text-[#ffcc00] hover:text-[#ffd700]">
              VIEW ALL &gt;&gt;
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {loading ? (
              Array(6).fill(0).map((_, i) => <ContentCardSkeleton key={i} />)
            ) : featured?.top_games?.length > 0 ? (
              featured.top_games.map((content) => (
                <ContentCard 
                  key={content.content_id}
                  content={content}
                  onClick={() => handleContentClick(content)}
                />
              ))
            ) : (
              <div className="col-span-full p-8 text-center text-[#666] border border-dashed border-[#333]">
                <Gamepad2 size={32} className="mx-auto mb-2 opacity-50" />
                <p>No games yet. Be the first to upload!</p>
              </div>
            )}
          </div>
        </section>

        {/* Top Videos */}
        <section data-testid="top-videos-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Film size={18} className="text-[#cc0000]" />
              TOP MOVIES
            </h2>
            <Link to="/videos" className="text-xs text-[#ffcc00] hover:text-[#ffd700]">
              VIEW ALL &gt;&gt;
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {loading ? (
              Array(6).fill(0).map((_, i) => <ContentCardSkeleton key={i} />)
            ) : featured?.top_videos?.length > 0 ? (
              featured.top_videos.map((content) => (
                <ContentCard 
                  key={content.content_id}
                  content={content}
                  onClick={() => handleContentClick(content)}
                />
              ))
            ) : (
              <div className="col-span-full p-8 text-center text-[#666] border border-dashed border-[#333]">
                <Film size={32} className="mx-auto mb-2 opacity-50" />
                <p>No movies yet. Be the first to upload!</p>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Latest Uploads */}
      {featured?.latest?.length > 0 && (
        <section data-testid="latest-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Clock size={18} className="text-[#999]" />
              LATEST UPLOADS
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {featured.latest.map((content) => (
              <ContentCard 
                key={content.content_id}
                content={content}
                onClick={() => handleContentClick(content)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Site Stats / Info */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-section">
        <div className="ws-stat-box">
          <div className="ws-stat-value">{featured?.top_games?.length || 0}</div>
          <div className="ws-stat-label">Games</div>
        </div>
        <div className="ws-stat-box">
          <div className="ws-stat-value">{featured?.top_videos?.length || 0}</div>
          <div className="ws-stat-label">Movies</div>
        </div>
        <div className="ws-stat-box">
          <div className="ws-stat-value">{featured?.vortex?.length || 0}</div>
          <div className="ws-stat-label">In Vortex</div>
        </div>
        <div className="ws-stat-box">
          <div className="ws-stat-value ws-logo-syndicate">2005</div>
          <div className="ws-stat-label">Est.</div>
        </div>
      </section>
    </div>
  );
}
